import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, useFieldArray } from "react-hook-form";
import { FormProvider, URHFTextField, RHFTextField } from "../hook-form";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
  TablePagination,
  Grid,
  Card,
} from "@mui/material";
import {
  Edit,
  Check,
  Close,
  Print,
  ShoppingCart,
  Send,
  Save,
  Cancel,
} from "@mui/icons-material";
import {
  getFetchInitSalesQuotation,
  createSaleQuotation,
  updateSaleQuotation,
  getFetchSalesQuotationById,
} from "../../services/api";
import { useNavigate, useParams } from "react-router-dom";
import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from "../../data/content";
import Navbar from "../../layout/Navbar";
import Sidebar from "../../layout/Sidebar";
import { useHasVerticalScroll } from "../../data/functions";

export default function SalesQuotationCreatePage() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const hasScroll = useHasVerticalScroll();

  const handleSidebarToggle = () => setSidebarOpen((open) => !open);
  const handleSidebarClose = () => setSidebarOpen(false);
  const baseWidth = hasScroll ? "99vw" : "100vw";
  const sidebarWidth = sidebarOpen
    ? SIDEBAR_WIDTH_EXPANDED
    : SIDEBAR_WIDTH_COLLAPSED;
  const navigate = useNavigate();
  const { quote_id } = useParams();

  const [initData, setInitData] = useState({});
  const [summaryData, setSummaryData] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const schema = Yup.object().shape({
    doc_number: Yup.string().required("Document number is required"),
    doc_type: Yup.string().required("Document type is required"),
    posting_date: Yup.string().required("Posting date is required"),
    doc_due_date: Yup.string().required("Due date is required"),
    customer_id: Yup.string().required("Customer is required"),
    project_id: Yup.string().required("Project is required"),
    contact_person: Yup.string().required("Contact person is required"),
    quote_type: Yup.string().required("Quote type is required"),
    place_of_supply: Yup.string().required("Place of supply is required"),
    sub_company_code: Yup.string().required(
      "Subsidiary company code is required"
    ),
    currency_code: Yup.string().required("Currency is required"),
    emp_id: Yup.string().required("Employee is required"),
    bill_address: Yup.string().required("Bill Address is required"),
    ship_address: Yup.string().required("Ship Address is required"),
    remarks: Yup.string(),
    t_c: Yup.string().required("Terms & Conditions is required"),
    items: Yup.array().of(
      Yup.object().shape({
        item_id: Yup.string().required("Required"),
        item_desc: Yup.string().required("Required"),
        quantity: Yup.number()
          .typeError("Quantity must be a number")
          .positive("Quantity must be greater than 0")
          .required("Required"),
        uom_id: Yup.string().required("Required"),
        hsn_code: Yup.string().required("Required"),
        rate: Yup.number()
          .typeError("Rate must be a number")
          .required("Required"),
        discount: Yup.number().typeError("Discount must be a number"),
        discount_amount: Yup.number().typeError(
          "Discount amount must be a number"
        ),
        tax_code: Yup.string().required("Required"),
        tax_amount: Yup.number().typeError("Tax amount must be a number"),
        amount: Yup.number()
          .typeError("Amount must be a number")
          .required("Required"),
      })
    ),
  });

  const defaultValues = {
    doc_number: "",
    doc_type: "",
    posting_date: "",
    doc_due_date: "",
    customer_id: "",
    project_id: "",
    contact_person: "",
    quote_type: "",
    place_of_supply: "",
    sub_company_code: "",
    currency_code: "",
    exchange_rate: "",
    emp_id: "",
    bill_address: "",
    ship_address: "",
    remarks: "",
    t_c: "",
    items: [
      {
        item_id: "",
        item_desc: "",
        quantity: "",
        uom_id: "",
        hsn_code: "",
        rate: "",
        discount: "",
        discount_amount: "",
        tax_code: "",
        tax_amount: "",
        amount: "",
      },
    ],
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { handleSubmit, reset, control, watch, setValue } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  useEffect(() => {
    const fetchDropdowns = async () => {
      setLoading(true);

      try {
        const initResponse = await getFetchInitSalesQuotation({
          module_name: "Sales quotation",
          is_update: quote_id != null,
        });
        // console.log(initResponse.data.data);
        setInitData(initResponse.data.data || {});

        let values = {
          ...defaultValues,
          doc_number: initResponse.data?.data?.series_id ?? "",
        };

        if (quote_id) {
          const quoteResponse = await getFetchSalesQuotationById(quote_id);
          values = {
            ...values,
            ...quoteResponse.data?.data,
          };
        }

        // console.log(values);

        reset(values);
      } catch (err) {
        console.error("Failed to fetch dropdown data", err);
      }

      setLoading(false);
      handleRecalculate();
    };

    fetchDropdowns();
  }, []);

  const handleRecalculate = () => {
    const items = watch("items");
    const updatedItems = calculateAmounts(items);

    updatedItems.forEach((item, idx) => {
      setValue(`items.${idx}.amount`, item.amount);
      setValue(`items.${idx}.tax_amount`, item.tax_amount);
    });

    const subTotal = updatedItems.reduce(
      (sum, it) => sum + (Number(it.gross_total) || 0),
      0
    );
    const taxTotal = updatedItems.reduce(
      (sum, it) => sum + (Number(it.tax_amount) || 0),
      0
    );

    const disTotal = updatedItems.reduce(
      (sum, it) => sum + (Number(it.discount_total) || 0),
      0
    );

    const grandTotal = updatedItems.reduce(
      (sum, it) => sum + (Number(it.amount) || 0),
      0
    );

    setSummaryData({
      sub_total: subTotal.toFixed(2),
      tax: taxTotal.toFixed(2),
      discount: disTotal.toFixed(2),
      total_amount: grandTotal.toFixed(2),
    });
  };

  const calculateAmounts = (items) => {
    return items.map((item) => {
      const qty = Number(item.quantity) || 0;
      const rate = Number(item.rate) || 0;
      const discountPct = Number(item.discount) || 0;
      const discountAmt = Number(item.discount_amount) || 0;
      const taxObj = initData?.taxcodes?.find((tc) => tc.id === item.tax_code);
      const taxPct = taxObj ? Number(taxObj.percentage) : 0;

      // Gross before discount
      const gross = qty * rate;

      // Discount calculations
      const discountPerValue = (gross * discountPct) / 100;

      // Net after discount
      const net = gross - discountPerValue - discountAmt;

      // Tax calculation (if using %)
      const taxAmount = ((net * taxPct) / 100).toFixed(2);

      // Final amount
      const amount = (net + Number(taxAmount)).toFixed(2);

      return {
        ...item,
        gross_total: gross.toFixed(2),
        discount_total: (discountPerValue + discountAmt).toFixed(2),
        tax_amount: taxAmount,
        amount: amount,
      };
    });
  };

  const onSubmit = async (data) => {
    setSubmitting(true);

    handleRecalculate();

    try {
      let response;

      if (quote_id) {
        response = await updateSaleQuotation(quote_id, data);
      } else {
        response = await createSaleQuotation({ ...data, ...summaryData });
      }

      //   console.log(response.data);

      if (response.data.success === true) {
        alert(
          quote_id
            ? "Quotation updated successfully!"
            : "Quotation created successfully!"
        );
        navigate(-1);
      } else {
        alert(response.data?.message ?? "Failed to create quotation");
      }
    } catch (err) {
      console.error("Error creating account:", err);
      alert(
        err.response.data?.message ?? "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: baseWidth,
        height: "auo",
        backgroundColor: "#f5f7fb",
        display: "flex",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={handleSidebarClose} />
      <Box>
        <Navbar onMenuClick={handleSidebarToggle} sidebarOpen={sidebarOpen} />

        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            width: `calc(${baseWidth} - ${sidebarWidth}px)`,
            minHeight: "calc( 100vh - 56px)",
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                backgroundColor: "#f5f8fc",
                minHeight: "calc( 100vh - 56px)",
                p: { xs: 2, sm: 3, md: 4 },
              }}
            >
              {/* Header with actions */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  📄 Sales Quotation Preparation
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button variant="outlined" size="small" startIcon={<Print />}>
                    Print
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Cancel />}
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<Save />}
                  >
                    Save Draft
                  </Button>
                  <Button
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<ShoppingCart />}
                  >
                    Convert to Order
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    size="small"
                    disabled={submitting}
                    startIcon={<Send />}
                  >
                    Submit
                  </Button>
                </Box>
              </Box>

              {/* The big form: each section is a Paper */}
              {/* Document Information */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Document Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <RHFTextField
                      name="doc_number"
                      label="Document Number*"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <RHFTextField name="doc_type" label="Document Type*" select>
                      <MenuItem value="Open">Open</MenuItem>
                      <MenuItem value="Closed">Closed</MenuItem>
                    </RHFTextField>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <RHFTextField
                      name="posting_date"
                      label="Posting Date*"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <RHFTextField
                      name="doc_due_date"
                      label="Document Due Date*"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Customer Information */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Customer Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                    <RHFTextField
                      name="customer_id"
                      label="Customer Name*"
                      select
                    >
                      {initData?.customers?.map((item, index) => (
                        <MenuItem key={index} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </RHFTextField>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                    <RHFTextField name="project_id" label="Project*" select>
                      {initData?.projects?.map((item, index) => (
                        <MenuItem key={index} value={item.id}>
                          {item.project_name}
                        </MenuItem>
                      ))}
                    </RHFTextField>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                    <RHFTextField
                      name="contact_person"
                      label="Contact Person*"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Quotation Details */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Quotation Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <RHFTextField
                      name="quote_type"
                      label="Quotation Type*"
                      select
                    >
                      <MenuItem value="Item">Item</MenuItem>
                      <MenuItem value="Service">Service</MenuItem>
                    </RHFTextField>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <RHFTextField
                      name="place_of_supply"
                      label="Place of Supply*"
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <RHFTextField
                      name="sub_company_code"
                      label="Subsidiary*"
                      select
                    >
                      {initData?.subcompanies?.map((item, index) => (
                        <MenuItem key={index} value={item.sub_company_code}>
                          {item.sub_company_name}
                        </MenuItem>
                      ))}
                    </RHFTextField>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <RHFTextField
                      name="currency_code"
                      label="Currency*"
                      select
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedCurrency = initData?.currency?.find(
                          (c) => c.currency_id === selectedId
                        );

                        setValue(
                          "exchange_rate",
                          selectedCurrency?.exchange_rate || ""
                        );
                      }}
                    >
                      {initData?.currency?.map((item, index) => (
                        <MenuItem key={index} value={item.currency_id}>
                          {item.currency}
                        </MenuItem>
                      ))}
                    </RHFTextField>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <RHFTextField
                      name="exchange_rate"
                      label="Exchange Rate"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Item Details */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Item Details
                  </Typography>
                </Box>

                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f1f3f6" }}>
                        <TableCell>S.No.</TableCell>
                        <TableCell>Item Code</TableCell>
                        <TableCell>Item Description</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell>UOM</TableCell>
                        <TableCell>HSN Code</TableCell>
                        <TableCell>Discount %</TableCell>
                        <TableCell>Discount Amt</TableCell>
                        <TableCell>Tax Code</TableCell>
                        <TableCell>Tax Amount</TableCell>
                        <TableCell>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((field, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <RHFTextField
                              name={`items.${index}.item_id`}
                              select
                            >
                              {initData?.items?.map((item, index) => (
                                <MenuItem key={index} value={item.item_id}>
                                  {item.item_name}
                                </MenuItem>
                              ))}
                            </RHFTextField>
                          </TableCell>

                          <TableCell>
                            <RHFTextField name={`items.${index}.item_desc`} />
                          </TableCell>

                          <TableCell>
                            <RHFTextField
                              name={`items.${index}.quantity`}
                              onBlur={handleRecalculate}
                            />
                          </TableCell>

                          <TableCell>
                            <RHFTextField
                              name={`items.${index}.rate`}
                              onBlur={handleRecalculate}
                            />
                          </TableCell>

                          <TableCell>
                            <RHFTextField name={`items.${index}.uom_id`} select>
                              {initData?.uom?.map((item, index) => (
                                <MenuItem key={index} value={item.id}>
                                  {item.uom_name}
                                </MenuItem>
                              ))}
                            </RHFTextField>
                          </TableCell>

                          <TableCell>
                            <RHFTextField name={`items.${index}.hsn_code`} />
                          </TableCell>

                          <TableCell>
                            <RHFTextField
                              type="number"
                              name={`items.${index}.discount`}
                              onBlur={handleRecalculate}
                            />
                          </TableCell>

                          <TableCell>
                            <RHFTextField
                              type="number"
                              name={`items.${index}.discount_amount`}
                              onBlur={handleRecalculate}
                            />
                          </TableCell>

                          <TableCell>
                            <RHFTextField
                              name={`items.${index}.tax_code`}
                              onChange={handleRecalculate}
                              select
                            >
                              {initData?.taxcodes?.map((item, index) => (
                                <MenuItem key={index} value={item.id}>
                                  {item.name}
                                </MenuItem>
                              ))}
                            </RHFTextField>
                          </TableCell>

                          <TableCell>
                            <RHFTextField
                              name={`items.${index}.tax_amount`}
                              InputProps={{ readOnly: true }}
                            />
                          </TableCell>

                          <TableCell>
                            <RHFTextField
                              name={`items.${index}.amount`}
                              InputProps={{ readOnly: true }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Button
                  variant="outlined"
                  size="small"
                  sx={{ my: 2 }}
                  onClick={() =>
                    append({
                      item_id: "",
                      item_desc: "",
                      quantity: "",
                      rate: "",
                      uom_id: "",
                      hsn_code: "",
                      discount: "",
                      discount_amount: "",
                      tax_code: "",
                      tax_amount: "",
                      amount: "",
                    })
                  }
                >
                  Add Row
                </Button>

                <Grid container spacing={2} my={3}>
                  <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                    <URHFTextField
                      label="Bill To*"
                      select
                      onChange={(e) => {
                        const selectedVendor = e.target.value;
                        setValue(
                          "bill_address",
                          selectedVendor.billing_address || ""
                        );
                      }}
                    >
                      {initData?.vendors?.map((item, index) => (
                        <MenuItem key={index} value={item}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </URHFTextField>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                    <URHFTextField
                      name="ship_to"
                      label="Ship To*"
                      select
                      onChange={(e) => {
                        const selectedVendor = e.target.value;
                        setValue(
                          "ship_address",
                          selectedVendor.shipping_address || ""
                        );
                      }}
                    >
                      {initData?.vendors?.map((item, index) => (
                        <MenuItem key={index} value={item}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </URHFTextField>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                    <RHFTextField name="emp_id" label="Sales Employee*" select>
                      {initData?.employees?.map((item, index) => (
                        <MenuItem key={index} value={item.emp_id}>
                          {`${item.first_name} ${item.last_name}`}
                        </MenuItem>
                      ))}
                    </RHFTextField>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                    <RHFTextField
                      name="bill_address"
                      label="Bill Address*"
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                    <RHFTextField
                      name="ship_address"
                      label="Ship Address*"
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                    <RHFTextField
                      name="remarks"
                      label="Remarks*"
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  sx={{ width: "100%", justifyContent: "space-around" }}
                >
                  {[
                    { label: "Sub Total", key: "sub_total" },
                    { label: "Tax", key: "tax" },
                    { label: "Discount", key: "discount" },
                    { label: "Total Amount", key: "total_amount" },
                  ].map((field) => (
                    <Grid item xs={12} sm={6} md={3} key={field.key}>
                      <Card elevation={0} sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {field.label}
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          ${summaryData?.[field.key] ?? "0.00"}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              {/* Terms & Conditions */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Terms & Conditions
                </Typography>

                <RHFTextField name="t_c" multiline rows={3} fullWidth />
              </Paper>
            </Box>
          </FormProvider>
        </Box>
      </Box>
    </Box>
  );
}
